import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { SEASON_MODE } from '@/config/seasonConfig';

// SEO Configuration for all pages
export const seoConfig = {
  siteName: 'WayBetter Landscaping & Snowplowing',
  siteUrl: 'https://waybetterlandandsnow.com',
  defaultTitle: 'WayBetter Landscaping & Snowplowing | Metro Milwaukee Lawn Care & Snow Removal',
  defaultDescription: 'Professional landscaping, lawn care, hardscaping, and 24/7 snow removal services in Metro Milwaukee. SBE-certified, serving Brookfield, Menomonee Falls, Waukesha & surrounding areas since 2014.',
  phone: '(414) 795-3610',
  address: 'N92W17420 Appleton Avenue, Suite 108, Menomonee Falls, WI 53051',
};

// Season-specific homepage SEO
const homeSeasonalSEO = {
  landscaping: {
    title: 'Landscaping & Lawn Care in Menomonee Falls, WI | WayBetter',
    description: 'Professional landscaping, lawn mowing, hardscapes, tree service & mulching in Menomonee Falls, Brookfield, New Berlin & Milwaukee. Free estimates. Call (414) 795-3610.',
  },
  snow: {
    title: 'WayBetter Landscaping & Snowplowing | Metro Milwaukee Lawn Care & Snow Removal',
    description: 'Professional landscaping, lawn care, hardscaping, and 24/7 snow removal services in Metro Milwaukee. SBE-certified, serving Brookfield, Menomonee Falls, Waukesha & surrounding areas. Free estimates!',
  }
};

// Page-specific SEO data
export const pageSEO = {
  home: {
    title: homeSeasonalSEO[SEASON_MODE].title,
    description: homeSeasonalSEO[SEASON_MODE].description,
    keywords: 'landscaping Milwaukee, lawn care Brookfield, snow removal Menomonee Falls, hardscape Waukesha, commercial landscaping Wisconsin',
    canonical: '/',
  },
  residential: {
    title: 'Residential Landscaping & Lawn Care Services | WayBetter Landscaping Milwaukee',
    description: 'Expert residential lawn care, landscape design, mulching, and seasonal maintenance for Metro Milwaukee homeowners. Weekly mowing, spring cleanup, fall aeration & more. Free estimates!',
    keywords: 'residential landscaping Milwaukee, lawn care service Brookfield, home lawn maintenance, landscape design Waukesha, mulching service, yard cleanup',
    canonical: '/residential',
  },
  commercial: {
    title: 'Commercial Landscaping & Property Maintenance | WayBetter Landscaping Milwaukee',
    description: 'Full-service commercial landscaping, grounds maintenance, parking lot sweeping, and property care for Milwaukee businesses. HOA, retail, and office building specialists. Get a quote!',
    keywords: 'commercial landscaping Milwaukee, property maintenance Wisconsin, HOA landscaping, parking lot sweeping, business lawn care, grounds maintenance',
    canonical: '/commercial',
  },
  snow: {
    title: '24/7 Snow Removal & Ice Management | WayBetter Snowplowing Milwaukee',
    description: 'Reliable 24/7 snow plowing, de-icing, and sidewalk clearing for Milwaukee homes and businesses. Commercial snow removal, salt spreading, and emergency storm response. Call now!',
    keywords: 'snow removal Milwaukee, snow plowing Brookfield, commercial snow removal, ice management Wisconsin, de-icing service, sidewalk clearing',
    canonical: '/snow-removal',
  },
  about: {
    title: 'About WayBetter Landscaping | SBE-Certified Milwaukee Landscaper Since 2014',
    description: 'Learn about WayBetter Landscaping & Snowplowing - a trusted, SBE-certified landscaping company serving Metro Milwaukee since 2014. Meet our team and discover our commitment to quality.',
    keywords: 'about WayBetter Landscaping, SBE certified landscaper Milwaukee, local landscaping company, Wisconsin landscaping business',
    canonical: '/about',
  },
  contact: {
    title: 'Contact WayBetter Landscaping | $75 Off + Free Assessment Milwaukee',
    description: 'Contact WayBetter Landscaping — get $75 off your first package with a free property assessment. Serving Metro Milwaukee for lawn care, landscaping, and snow removal. Call (414) 795-3610!',
    keywords: 'contact landscaper Milwaukee, free landscaping estimate, lawn care quote Brookfield, snow removal quote, landscaping consultation',
    canonical: '/contact',
  },
  gallery: {
    title: 'Project Gallery | Landscaping & Snow Removal Photos | WayBetter Milwaukee',
    description: 'Browse our portfolio of completed landscaping projects, hardscape installations, lawn care transformations, and snow removal work across Metro Milwaukee. See our quality work!',
    keywords: 'landscaping photos Milwaukee, hardscape gallery, lawn care before after, retaining wall photos, snow removal portfolio',
    canonical: '/gallery',
  },
  blog: {
    title: 'Lawn Care Tips & Landscaping Blog | WayBetter Landscaping Milwaukee',
    description: 'Expert lawn care tips, seasonal landscaping advice, and how-to guides from Milwaukee\'s trusted landscapers. Learn about mowing, mulching, snow prep, and more!',
    keywords: 'lawn care tips, landscaping blog Milwaukee, how to mow lawn, when to aerate, mulching guide, Wisconsin gardening tips',
    canonical: '/blog',
  },
  // Service landing pages
  landscapeServices: {
    title: 'Landscape Services Milwaukee | Design, Installation & Maintenance',
    description: 'Complete landscape services including design, installation, planting, and ongoing maintenance for Metro Milwaukee properties. Transform your outdoor space today!',
    keywords: 'landscape services Milwaukee, landscape design, planting service, outdoor design Wisconsin',
    canonical: '/services/landscape-services',
  },
  landscapeDesign: {
    title: 'Landscape Design Milwaukee | Custom Outdoor Living Spaces',
    description: 'Professional landscape design services creating beautiful, functional outdoor spaces for Milwaukee homeowners. Custom designs, 3D renderings, and expert installation.',
    keywords: 'landscape design Milwaukee, custom landscaping, outdoor living design, garden design Wisconsin',
    canonical: '/services/landscape-design',
  },
  hardscape: {
    title: 'Hardscape Installation Milwaukee | Patios, Walkways & Retaining Walls',
    description: 'Expert hardscape installation including paver patios, stone walkways, retaining walls, and outdoor living spaces in Metro Milwaukee. Quality craftsmanship guaranteed!',
    keywords: 'hardscape Milwaukee, paver patio installation, retaining wall builder, stone walkway, outdoor living space',
    canonical: '/services/hardscape',
  },
  lawnMaintenance: {
    title: 'Lawn Maintenance Milwaukee | Weekly Mowing & Yard Care Services',
    description: 'Professional lawn maintenance services including weekly mowing, edging, trimming, and seasonal care for Milwaukee homes. Keep your lawn looking its best year-round!',
    keywords: 'lawn maintenance Milwaukee, weekly mowing service, yard care Brookfield, grass cutting, lawn edging service',
    canonical: '/services/lawn-maintenance',
  },
  treeService: {
    title: 'Tree Service Milwaukee | Trimming, Removal & Stump Grinding',
    description: 'Professional tree services including trimming, pruning, removal, and stump grinding for Metro Milwaukee. Safe, insured, and experienced tree care specialists.',
    keywords: 'tree service Milwaukee, tree trimming, tree removal Wisconsin, stump grinding, tree pruning',
    canonical: '/services/tree-service',
  },
  powerSweeping: {
    title: 'Commercial Power Sweeping Milwaukee | Parking Lot Cleaning Services',
    description: 'Professional parking lot sweeping and power sweeping services for Milwaukee businesses. Keep your commercial property clean and safe. Regular and one-time service available.',
    keywords: 'power sweeping Milwaukee, parking lot sweeping, commercial lot cleaning, street sweeping Wisconsin',
    canonical: '/services/power-sweeping',
  },
};

// SEO Component
export const SEO = ({ 
  title, 
  description, 
  keywords, 
  canonical,
  type = 'website',
  image,
  article = null,
  robots = 'index, follow', // Default to index, follow for all real pages
}) => {
  const pageTitle = title || seoConfig.defaultTitle;
  const pageDescription = description || seoConfig.defaultDescription;
  const pageUrl = `${seoConfig.siteUrl}${canonical || ''}`;
  const pageImage = image || `${seoConfig.siteUrl}/og-image.jpg`;

  // Remove duplicate canonical tags on mount (handles SSR/hydration mismatch)
  useEffect(() => {
    const canonicals = document.querySelectorAll('link[rel="canonical"]');
    if (canonicals.length > 1) {
      // Keep only the last one (Helmet-managed)
      for (let i = 0; i < canonicals.length - 1; i++) {
        canonicals[i].remove();
      }
    }
  }, [pageUrl]);

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{pageTitle}</title>
      <meta name="title" content={pageTitle} />
      <meta name="description" content={pageDescription} />
      {keywords && <meta name="keywords" content={keywords} />}
      <meta name="robots" content={robots} />
      <link rel="canonical" href={pageUrl} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={pageUrl} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:image" content={pageImage} />
      <meta property="og:site_name" content={seoConfig.siteName} />

      {/* Twitter */}
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:url" content={pageUrl} />
      <meta property="twitter:title" content={pageTitle} />
      <meta property="twitter:description" content={pageDescription} />
      <meta property="twitter:image" content={pageImage} />

      {/* Article specific meta tags */}
      {article && (
        <>
          <meta property="article:published_time" content={article.publishedTime} />
          <meta property="article:author" content={article.author} />
          <meta property="article:section" content={article.section} />
        </>
      )}
    </Helmet>
  );
};

// Blog Post SEO Component with BlogPosting structured data
export const BlogPostSEO = ({ post }) => {
  const postUrl = post ? `${seoConfig.siteUrl}/blog/${post.slug}` : '';

  // Remove duplicate canonical tags on mount
  useEffect(() => {
    if (!post) return;
    const canonicals = document.querySelectorAll('link[rel="canonical"]');
    if (canonicals.length > 1) {
      for (let i = 0; i < canonicals.length - 1; i++) {
        canonicals[i].remove();
      }
    }
  }, [postUrl, post]);

  if (!post) return null;

  // Parse the date string (e.g., "March 5, 2026") to ISO format
  const parseDate = (dateStr) => {
    try {
      const date = new Date(dateStr);
      return date.toISOString();
    } catch {
      return new Date().toISOString();
    }
  };

  const isoDate = parseDate(post.date);

  // BlogPosting schema (more specific than Article for blog posts)
  const blogPostingSchema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": post.title,
    "description": post.excerpt,
    "url": postUrl,
    "image": `${seoConfig.siteUrl}${post.image}`,
    "datePublished": isoDate,
    "dateModified": isoDate,
    "author": {
      "@type": "Organization",
      "name": "WayBetter Landscaping & Snowplowing LLC",
      "url": seoConfig.siteUrl
    },
    "publisher": {
      "@type": "Organization",
      "name": "WayBetter Landscaping & Snowplowing LLC",
      "logo": {
        "@type": "ImageObject",
        "url": `${seoConfig.siteUrl}/logo.png`
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": postUrl
    }
  };

  // Create title under 60 chars
  const pageTitle = post.title.length > 55 
    ? `${post.title.substring(0, 52)}...` 
    : post.title;
  const fullTitle = `${pageTitle} | WayBetter`;

  // Create description between 140-158 chars
  let metaDescription = post.excerpt;
  if (metaDescription.length > 158) {
    metaDescription = metaDescription.substring(0, 155) + '...';
  } else if (metaDescription.length < 140) {
    metaDescription = `${metaDescription} Expert lawn care tips from WayBetter Landscaping, Metro Milwaukee.`;
    if (metaDescription.length > 158) {
      metaDescription = metaDescription.substring(0, 155) + '...';
    }
  }

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={metaDescription} />
      <meta name="keywords" content={`${post.category}, lawn care tips, landscaping advice, Wisconsin gardening`} />
      <meta name="robots" content="index, follow" />
      <link rel="canonical" href={postUrl} />

      {/* Open Graph */}
      <meta property="og:type" content="article" />
      <meta property="og:url" content={postUrl} />
      <meta property="og:title" content={post.title} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:image" content={`${seoConfig.siteUrl}${post.image}`} />
      <meta property="article:published_time" content={isoDate} />
      <meta property="article:modified_time" content={isoDate} />
      <meta property="article:section" content={post.category} />
      <meta property="article:author" content="WayBetter Landscaping & Snowplowing LLC" />

      {/* Twitter */}
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:url" content={postUrl} />
      <meta property="twitter:title" content={post.title} />
      <meta property="twitter:description" content={metaDescription} />
      <meta property="twitter:image" content={`${seoConfig.siteUrl}${post.image}`} />

      {/* BlogPosting Structured Data - NO aggregateRating */}
      <script type="application/ld+json">
        {JSON.stringify(blogPostingSchema)}
      </script>
    </Helmet>
  );
};

export default SEO;
